import React, { useEffect, useState } from 'react';
import { authFetch, API } from '../api';

// Statistics → Picker pace (Fernando, 2026-09-30): each person's split of a tablet step into
// walking (m/s), time per stop and per piece, and the box change — fitted from their own clean
// steps once they have enough, the warehouse average until then (labelled). Nobody's pace is the
// standard; the 20 orders/hour target is the fixed line on the rounds table. Data: GET /api/pp/pace
// (backend/pp_pace.py). Claude, 2026-09-30.

interface Fit { per_stop: number; s_per_metre: number; per_piece: number; speed: number; n: number; r2: number }
interface Person {
  display_name: string; source: 'own' | 'warehouse' | null; fit: Fit | null; own: Fit | null;
  clean_steps: number; rounds: number; tapped_through_rounds: number; since: string | null;
  box_change_sec: number | null; checks: number;
}
interface Pace {
  people: Record<string, Person>; warehouse: Fit | null; warehouse_clean_steps: number;
  warehouse_box_change_sec: number | null; excluded_rounds: Record<string, number[]>; rounds_uncached: number;
  computed_at: string; rules: { min_clean_steps: number; step_seconds: number[]; tap_through: string; speed_band: number[] };
  error?: string;
}

const sec = (v: number | null | undefined) => (v == null ? '—' : `${Math.round(v)} s`);

export default function PickerPace() {
  const [p, setP] = useState<Pace | null>(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    authFetch(`${API}/pp/pace`).then(async r => {
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setErr(d.detail || `HTTP ${r.status}`); return; }
      setP(d);
    }).catch(() => setErr('Could not load the pace'));
  }, []);
  if (err) return <div className="mt-6 text-sm text-red-400">{err}</div>;
  if (!p) return <div className="mt-6 text-sm text-gray-500">Loading the picker pace…</div>;
  const rows = Object.entries(p.people).sort((a, b) => b[1].clean_steps - a[1].clean_steps);
  const wh = p.warehouse;
  return (
    <div className="mt-6 bg-[#1a1d27] border border-gray-800 rounded-xl overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-800">
        <div className="text-white font-semibold text-sm">Picker pace — walking and picking split</div>
        <p className="text-gray-500 text-xs mt-0.5">
          A tablet step is the walk to a stop plus the picking there. Over a person's clean steps (after Next, {p.rules.step_seconds[0]}–{p.rules.step_seconds[1]} s,
          metres known) the time is split into walking at their speed, a fixed part per stop and a part per piece. A person gets their own pace
          from {p.rules.min_clean_steps} clean steps; until then the warehouse average stands in. Rounds tapped through ({p.rules.tap_through}) are left out.
          Nobody's pace is the standard — the target is 20 orders/hour on the rounds table.
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-gray-500 text-left text-xs">
            <tr>
              <th className="px-4 py-2 font-medium">Person</th>
              <th className="px-3 py-2 font-medium text-right" title="1 / seconds per metre">Walking</th>
              <th className="px-3 py-2 font-medium text-right" title="Fixed time at every stop">Per stop</th>
              <th className="px-3 py-2 font-medium text-right" title="Added time per piece picked">Per piece</th>
              <th className="px-3 py-2 font-medium text-right" title="Median swap time minus the walk to the station">Box change</th>
              <th className="px-3 py-2 font-medium text-right" title="Clean steps the fit used · rounds">Data</th>
              <th className="px-3 py-2 font-medium">Pace used</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(([user, x]) => {
              const f = x.fit;
              return (
                <tr key={user} className="border-t border-gray-800/60">
                  <td className="px-4 py-2 text-gray-100">{x.display_name || user}</td>
                  <td className="px-3 py-2 text-right text-gray-200">{f ? `${f.speed} m/s` : '—'}</td>
                  <td className="px-3 py-2 text-right text-gray-200">{f ? sec(f.per_stop) : '—'}</td>
                  <td className="px-3 py-2 text-right text-gray-200">{f ? sec(f.per_piece) : '—'}</td>
                  <td className="px-3 py-2 text-right text-gray-200">{sec(x.box_change_sec)}</td>
                  <td className="px-3 py-2 text-right text-gray-400 text-xs whitespace-nowrap">{x.clean_steps} steps · {x.rounds} rounds{x.tapped_through_rounds ? ` (+${x.tapped_through_rounds} tapped through)` : ''}{x.since ? ` · since ${x.since}` : ''}</td>
                  <td className="px-3 py-2 text-xs whitespace-nowrap">
                    {x.source === 'own'
                      ? <span className="text-emerald-300" title={`Own fit: R² ${f?.r2}`}>own</span>
                      : x.source === 'warehouse'
                        ? <span className="text-amber-300" title={x.own ? `Own fit exists (${x.own.speed} m/s, ${x.own.n} steps) but under ${p.rules.min_clean_steps} clean steps` : 'No plausible own fit yet'}>warehouse average{x.own ? ` · own so far ${x.own.speed} m/s` : ''}</span>
                        : <span className="text-gray-500">none yet</span>}
                  </td>
                </tr>
              );
            })}
            <tr className="border-t border-gray-700 bg-gray-800/30">
              <td className="px-4 py-2 text-gray-300">Warehouse average</td>
              <td className="px-3 py-2 text-right text-gray-300">{wh ? `${wh.speed} m/s` : '—'}</td>
              <td className="px-3 py-2 text-right text-gray-300">{wh ? sec(wh.per_stop) : '—'}</td>
              <td className="px-3 py-2 text-right text-gray-300">{wh ? sec(wh.per_piece) : '—'}</td>
              <td className="px-3 py-2 text-right text-gray-300">{sec(p.warehouse_box_change_sec)}</td>
              <td className="px-3 py-2 text-right text-gray-500 text-xs">{p.warehouse_clean_steps} steps{p.rounds_uncached ? ` · ${p.rounds_uncached} rounds not yet in` : ''}</td>
              <td className="px-3 py-2 text-xs text-gray-500">everyone's clean steps</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div className="px-4 py-2 text-[11px] text-gray-600 border-t border-gray-800">Computed {p.computed_at.replace('T', ' ')} UTC · floor plan {'layout' in p ? (p as any).layout : ''} · refreshed every 10 minutes</div>
    </div>
  );
}
